import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Wrench, ReceiptText, Camera, Car, Phone, Mail, FileText, ShieldCheck, Megaphone, MessagesSquare, Download, X, Lock, Building2, Siren, Printer, CreditCard, CalendarDays, Copy, Check, Home, CircleCheck, Landmark, Repeat, Dumbbell, Sprout, Laptop, BedDouble, Sofa, Clapperboard, type LucideIcon } from 'lucide-react';
import { api, invoiceNumberLabel, openInvoiceDocument } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { Badge, Money, Button, Field, Input, Textarea, Select, Modal, SkeletonRows } from '../../components/ui';
import { formatDate, formatMoney } from '../../lib/viz';
import { bankingApi, monthLabel, type RentAccount } from '../../lib/banking';
import { parkingApi } from '../../lib/parking';
import { openStoredFile } from '../../lib/reservations';
import { documentsApi, portalApi, toUpload, fileSize, dueTone, type MyBuilding, type DocumentItem, type Upload } from '../../lib/resident';
import { PlanSnippet } from '../../components/parking/PlanSnippet';
import { PageLoader } from '../../components/BrandLoader';
import { MyDepositCard } from '../../components/resident/MyDepositCard';
import { TPageHead, TCard, TSection, TRow, TList, TLinkButton, THero, TTile, TBanner, TFacts, TEmpty } from '../../components/tenant/kit';
import { TGallery } from '../../components/tenant/Gallery';
import { formatPhone } from '../../lib/phone';

type R = Record<string, any>;
const SECTIONS = ['home', 'rent', 'pay', 'repairs', 'documents', 'building', 'spaces'] as const;
type Section = (typeof SECTIONS)[number];

/**
 * The resident portal (prototype §26b): Home · Rent · Repairs · Documents · Messages · Building, plus Pay rent and Book a
 * space (reached from Home). Same application,
 * same login — the permission set routes a resident here; every call below is scoped server-side to their own records.
 * The frame (top bar on a laptop, the floating tab bar on a phone) is TenantShell; the look is the phone app's.
 */
export default function MyHomePage() {
  const { section: raw } = useParams<{ section?: string }>();
  const section: Section = (SECTIONS as readonly string[]).includes(raw ?? 'home') ? (raw ?? 'home') as Section : 'home';
  const tenancies = useQuery({ queryKey: ['my-tenancies'], queryFn: async () => (await api.get<R[]>('/tenancies/mine')).data });
  const t = tenancies.data?.find((x) => ['ACTIVE', 'NOTICE_GIVEN'].includes(x.status)) ?? tenancies.data?.[0];

  if (raw && !(SECTIONS as readonly string[]).includes(raw)) return <Navigate to="/app/my-home" replace />;
  if (tenancies.isLoading) return <PageLoader />;
  if (!t) return <TEmpty icon={Home} title="No tenancy on this account yet" body="Once your tenancy is set up, your home, rent, documents and repairs will appear here." />;

  return (
    <div className="space-y-4 sm:space-y-5">
      {section === 'home' && <HomeSection t={t} />}
      {section === 'rent' && <RentSection t={t} />}
      {section === 'pay' && <PaySection t={t} />}
      {section === 'spaces' && <SpacesSection />}
      {section === 'repairs' && <RepairsSection t={t} />}
      {section === 'documents' && <DocumentsSection />}
      {section === 'building' && <BuildingSection />}
    </div>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

const useMyBuilding = () => useQuery({ queryKey: ['/my-building'], queryFn: portalApi.myBuilding });
const useMyDocs = () => useQuery({ queryKey: ['/documents/mine'], queryFn: documentsApi.mine });
const useJobs = () => useQuery({ queryKey: ['my-jobs'], queryFn: async () => (await api.get<R[]>('/work-orders/mine')).data });
const useRentAccount = (id?: string) => useQuery({ queryKey: ['my-rent-account', id], queryFn: () => bankingApi.rentAccount(id!), enabled: !!id });

const ordinal = (n: number) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return s[(v - 20) % 10] ?? s[v] ?? s[0]; };
const tel = (p: string) => `tel:${p.replace(/[^\d+]/g, '')}`;
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

/** What the rent card says (the app's rentDue): anything owing first, then the next instalment, else the rent itself. */
function rentDue(t: R, ra: RentAccount | undefined) {
  const cur = ra?.currency ?? t.rentCurrency;
  if (ra && ra.totals.owingMinor > 0) {
    const oldest = ra.months.filter((m) => m.outstandingMinor > 0).sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0];
    return { overline: 'Rent due', amount: formatMoney(ra.totals.owingMinor, cur), when: oldest ? `due ${formatDate(oldest.dueDate)}` : 'due now', owing: true };
  }
  if (ra?.next) return { overline: 'Next rent', amount: formatMoney(ra.next.amountMinor - ra.next.paidMinor, cur), when: `due ${formatDate(ra.next.dueDate)}`, owing: false };
  return { overline: 'Your rent', amount: formatMoney(t.rentMinor, cur), when: `per ${String(t.rentPeriod ?? 'month').toLowerCase()}`, owing: false };
}

/** A tel: link as a small pill (the emergency line, the building's numbers). `round`: just the phone icon on a phone. */
function CallPill({ phone, urgent, round }: { phone: string; urgent?: boolean; round?: boolean }) {
  return (
    <a href={tel(phone)} aria-label={`Call ${formatPhone(phone)}`} className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-full text-[14px] font-semibold transition ${round ? 'px-0 sm:px-4' : 'px-4'} ${urgent ? 'bg-[#A85A4C] text-white hover:bg-[#8F4A3E]' : 'bg-bronze-50 text-ink-900 hover:bg-bronze-100'}`}>
      <Phone className="h-4 w-4" /><span className={round ? 'hidden sm:inline' : ''}>{formatPhone(phone)}</span>
    </a>
  );
}

// ─── Home ────────────────────────────────────────────────────────────────────

function HomeSection({ t }: { t: R }) {
  const user = useAuth((s) => s.user);
  const docs = useMyDocs();
  const building = useMyBuilding();
  const jobs = useJobs();
  // Every figure here comes from the rent account — the Rent page and the app's rent card read the same one. (The AR
  // invoices behind /invoices/mine can lag it: a receipt not matched to an invoice settles the rent but leaves the invoice open.)
  const ra = useRentAccount(t.id).data;
  const due = rentDue(t, ra);
  const dep = docs.data?.deposits.find((d) => d.tenancyId === t.id);
  const b = building.data?.find((x) => x.tenancyId === t.id) ?? building.data?.[0];
  const ooh = b?.contacts.find((c) => c.emergency && c.phone);
  const open = (jobs.data ?? []).filter((j) => j.isOpen);
  const flat = ra?.unitReference ?? t.unit?.reference;
  const home = ra?.propertyName ?? t.property?.name;
  const live = ['ACTIVE', 'NOTICE_GIVEN'].includes(t.status);

  return (<>
    <TPageHead overline={greeting()} title={`Hello, ${user?.firstName ?? 'there'}`} />

    <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr] lg:gap-5">
      <THero overline={due.overline} value={ra ? due.amount : <span className="inline-block h-12 w-40 animate-pulse rounded-xl bg-white/70" />} note={ra ? due.when : null} alert={due.owing}
        meta={<>
          {[flat ? `Flat ${flat}` : null, home].filter(Boolean).join(' · ')}
          {!live && t.startDate && <span className="block">Your tenancy starts on {formatDate(t.startDate)}.</span>}
          {t.status === 'NOTICE_GIVEN' && <span className="mt-2 block"><Badge value={`Moving out ${formatDate(t.endDate)}`} tone="warning" /></span>}
        </>}>
        <TLinkButton to="/app/my-home/pay" icon={CreditCard} className="w-full sm:w-auto sm:min-w-[220px]">Pay rent</TLinkButton>
      </THero>

      <div className="grid grid-cols-4 gap-2.5 lg:grid-cols-2 lg:gap-4">
        <TTile i={1} label="Pay rent" icon={CreditCard} to="/app/my-home/pay" />
        <TTile i={2} label="Report a repair" icon={Wrench} to="/app/my-home/repairs?report=1" />
        <TTile i={3} label="Book a space" icon={CalendarDays} to="/app/my-home/spaces" soon />
        <TTile i={4} label="Documents" icon={FileText} to="/app/my-home/documents" />
      </div>
    </div>

    {!!b?.notices.length && (<>
      <TSection action={b.notices.length > 2 ? <Link to="/app/my-home/building" className="inline-flex min-h-[44px] items-center text-[14px] font-semibold text-bronze-700 hover:underline">All {b.notices.length}</Link> : undefined}>Notices</TSection>
      <TCard flush i={2}>
        <TList>{b.notices.slice(0, 2).map((n) => (
          <TRow key={n.id} icon={Megaphone} title={n.title}
            sub={<><span className="line-clamp-2 whitespace-pre-line">{n.body}</span><span className="mt-1 block text-[12px] text-ink-400">{n.propertyId ? '' : 'Every building · '}{n.publishedAt ? formatDate(n.publishedAt) : ''}</span></>} />
        ))}</TList>
      </TCard>
    </>)}

    <TSection>Your home</TSection>
    <div className="grid items-start gap-4 md:grid-cols-2 lg:gap-5">
      <TCard title="Your tenancy" sub={t.reference} i={3}>
        <TFacts items={[
          { k: 'Flat', v: `${flat ?? '—'} · ${home ?? ''}` },
          { k: 'From', v: formatDate(t.startDate) },
          { k: 'Until', v: t.endDate ? formatDate(t.endDate) : 'Rolling (periodic)' },
          { k: 'Rent', v: <><Money minor={t.rentMinor} currency={t.rentCurrency} /> a {String(t.rentPeriod ?? 'month').toLowerCase()}, on the {t.rentDueDay}{ordinal(t.rentDueDay)}</> },
          { k: 'Deposit', v: dep ? <span className="inline-flex flex-wrap items-center justify-end gap-2"><Money minor={dep.amountMinor} currency={dep.currency} /><Badge value="PROTECTED" /></span> : <span className="font-normal text-ink-500">Being protected</span> },
          { k: 'On the tenancy', v: (t.tenants ?? []).map((x: R) => x.name).join(', ') || '—' },
        ]} />
        <Link to="/app/my-home/rent#deposit" className="mt-3 inline-flex min-h-[44px] items-center text-[14px] font-semibold text-bronze-700 hover:underline">Your deposit in full</Link>
      </TCard>

      <div className="space-y-4 lg:space-y-5">
        <TCard title="Something urgent?" sub="A gas leak, a fire or a flood: ring first, then report it here so there is a record." i={4}>
          {ooh ? (
            <div className="flex flex-wrap items-center gap-3 rounded-[16px] bg-[#F0DED9]/70 p-3.5">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#A85A4C] text-white"><Siren className="h-5 w-5" /></span>
              <span className="min-w-[10rem] flex-1"><span className="block text-[12px] font-semibold uppercase tracking-[0.1em] text-[#7A2E22]">{ooh.role}</span><span className="block text-[13px] text-ink-600">{ooh.name}</span></span>
              <span className="w-full sm:w-auto [&>a]:w-full [&>a]:justify-center"><CallPill phone={ooh.phone!} urgent /></span>
            </div>
          ) : <p className="text-[14px] text-ink-500">No out-of-hours number published yet.</p>}
        </TCard>
        <TCard flush i={5}>
          <TList>
            <TRow icon={MessagesSquare} title={home ? `The ${home} team` : 'The building team'} sub={live ? 'Questions, keys, deliveries, anything' : 'Messages open once you move in'} to="/app/messages" />
            <TRow icon={Wrench} title="Your repairs" sub={jobs.isLoading ? 'Loading…' : open.length ? `${open.length} being worked on` : 'Nothing outstanding'} to="/app/my-home/repairs" />
            {b?.parking && <TRow icon={Car} title={`Parking bay ${b.parking.bay}`} sub={[b.parking.kind, b.parking.vehicleReg].filter(Boolean).join(' · ')} to="/app/my-home/building" />}
          </TList>
        </TCard>
      </div>
    </div>
  </>);
}

// ─── Rent ────────────────────────────────────────────────────────────────────

function RentSection({ t }: { t: R }) {
  const ra = useRentAccount(t.id).data;
  const invoices = useQuery({ queryKey: ['my-invoices'], queryFn: async () => (await api.get<R[]>('/invoices/mine')).data });
  const [copied, setCopied] = useState(false);
  // Rent is the rent account above; only non-rent invoices still owing are "other charges".
  const otherCharges = (invoices.data ?? []).filter((i) => i.invoiceType !== 'RENT' && i.outstandingMinor > 0);
  const due = rentDue(t, ra);
  const copy = (v: string) => { void navigator.clipboard?.writeText(v).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => undefined); };

  return (<>
    <TPageHead title="Rent" sub={`${t.property?.name} · Flat ${t.unit?.reference} · due on the ${t.rentDueDay}${ordinal(t.rentDueDay)} of each month`}
      action={<TLinkButton to="/app/my-home/pay" icon={CreditCard} small>Pay rent</TLinkButton>} />
    {!ra ? <TCard><SkeletonRows rows={5} cols={3} /></TCard> : (<>
      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr] lg:gap-5">
        <THero overline={due.overline} value={due.amount} note={due.when} alert={due.owing}
          meta={ra.totals.owingMinor > 0 ? `${ra.totals.monthsOwed} month${ra.totals.monthsOwed === 1 ? '' : 's'} still open — pay by bank transfer quoting your reference.` : 'You are up to date. Thank you.'}>
          <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-ink-500">Your payment reference</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <span className="break-all text-[24px] font-semibold tracking-[0.06em] text-ink-900">{ra.payReference ?? '—'}</span>
            {ra.payReference && (
              <button type="button" onClick={() => copy(ra.payReference!)} className="t-btn t-btn-soft t-btn-sm" aria-live="polite">
                {copied ? <><Check className="h-4 w-4" />Copied</> : <><Copy className="h-4 w-4" />Copy</>}
              </button>
            )}
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-ink-600">Quote it exactly on every transfer or standing order, so your payment is matched to your home.</p>
        </THero>

        <TCard title="Pay to" sub="By bank transfer or standing order" i={1}>
          {ra.payTo ? (
            <TFacts items={[
              { k: 'Account name', v: ra.payTo.accountName },
              { k: 'Sort code', v: <span className="font-mono">{ra.payTo.sortCode ?? '—'}</span> },
              { k: 'Account number', v: <span className="font-mono">{ra.payTo.accountNumber ?? ra.payTo.iban ?? '—'}</span> },
              ...(ra.payTo.bankName ? [{ k: 'Bank', v: ra.payTo.bankName }] : []),
            ]} />
          ) : <p className="text-[14px] text-ink-500">Ask us for the bank details through Messages.</p>}
        </TCard>
      </div>

      <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
        {[
          ['Charged', ra.totals.chargedMinor, `${ra.totals.monthsCharged} months`, ''],
          ['Received', ra.totals.receivedMinor, `${ra.totals.monthsPaid} paid`, 'text-[#3F6340]'],
          ['Owing', ra.totals.owingMinor, ra.totals.monthsOwed ? `${ra.totals.monthsOwed} month${ra.totals.monthsOwed === 1 ? '' : 's'}` : 'Up to date', ra.totals.owingMinor ? 'text-[#8A3F33]' : ''],
        ].map(([k, v, s, c], n) => (
          <div key={String(k)} className="t-card t-rise px-3 py-4 text-center sm:px-5 sm:py-5" style={{ '--i': n + 1 } as React.CSSProperties}>
            <div className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-500">{k}</div>
            <Money minor={v as number} currency={ra.currency} className={`t-serif mt-1 block text-[24px] leading-tight text-ink-900 sm:text-[30px] ${c}`} />
            <div className="mt-0.5 text-[12.5px] text-ink-500">{s}</div>
          </div>
        ))}
      </div>

      <TCard title="Month by month" sub="The last 12 months, as our bank sees them" flush i={2}>
        <TList>
          {ra.months.slice(0, 12).map((m) => (
            <li key={m.id} className="flex min-h-[60px] items-center justify-between gap-3 px-5 py-2.5">
              <span className="min-w-0"><span className="block text-[15px] font-semibold text-ink-900">{monthLabel(m.month)}</span><span className="block text-[13px] text-ink-500">Due {formatDate(m.dueDate)}</span></span>
              <span className="flex shrink-0 items-center gap-2.5">
                {m.outstandingMinor > 0 ? <Money minor={m.outstandingMinor} currency={ra.currency} className="text-[15px] font-semibold text-[#8A3F33]" /> : <Money minor={m.paidMinor} currency={ra.currency} className="text-[15px] text-ink-700" />}
                <Badge value={m.outstandingMinor === 0 ? 'PAID' : m.paidMinor > 0 ? 'PARTIAL' : m.status} />
              </span>
            </li>))}
          {!ra.months.length && <li className="px-5 py-8 text-center text-[14px] text-ink-500">No rent has fallen due yet.</li>}
        </TList>
      </TCard>

      {ra.receipts.length > 0 && (
        <TCard title="Payments received" flush i={3}>
          <TList>{ra.receipts.slice(0, 6).map((r) => (
            <TRow key={r.id} icon={CircleCheck} tone="good" title={<Money minor={r.amountMinor} currency={r.currency} />} sub={`${formatDate(r.receivedOn)} · ${r.fromBank ? 'bank transfer' : r.method.replace(/_/g, ' ').toLowerCase()}`} />
          ))}</TList>
        </TCard>
      )}
    </>)}

    {otherCharges.length > 0 && (
      <TCard title="Other charges" flush>
        <TList>{otherCharges.map((i) => (
          <TRow key={i.id} icon={ReceiptText} title={invoiceNumberLabel(i.number)} sub={`Due ${formatDate(i.dueDate)}`}
            trailing={<><Money minor={i.outstandingMinor} currency={i.currency} className="text-[15px] font-semibold text-[#8A3F33]" /><Badge value={i.status} /></>} />
        ))}</TList>
      </TCard>
    )}

    <div id="deposit" className="scroll-mt-24"><MyDepositCard tenancyId={t.id} /></div>

    <p className="px-1 text-[13px] leading-relaxed text-ink-500">Something not right? <Link to="/app/messages" className="font-semibold text-bronze-700 hover:underline">Message the team</Link>. Card payment is coming soon — for now, <Link to="/app/my-home/pay" className="font-semibold text-bronze-700 hover:underline">pay by bank transfer</Link>.</p>
  </>);
}

// ─── Pay rent ────────────────────────────────────────────────────────────────

/** Copy to the clipboard; `copied` names the last thing copied for a moment. */
function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (key: string, v: string) => void navigator.clipboard?.writeText(v).then(() => { setCopied(key); setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800); }).catch(() => undefined);
  return { copied, copy };
}

/**
 * Pay rent (the app's resident/rent.tsx): what is due now and next, from the rent account; how to pay by bank transfer
 * — the payee, sort code, account number and the payment reference, each with a copy button — and a standing order.
 * There is no card gateway yet (it says so, as the app does) and no "I've paid" for a resident: a payment is matched
 * from the bank statement and then shows on the Rent page.
 */
function PaySection({ t }: { t: R }) {
  const q = useRentAccount(t.id);
  const ra = q.data;
  const { copied, copy } = useCopy();
  const due = rentDue(t, ra);
  const cur = ra?.currency ?? t.rentCurrency;
  const dueDay = t.rentDueDay ? `the ${t.rentDueDay}${ordinal(t.rentDueDay)}` : null;
  const nextLeft = ra?.next ? ra.next.amountMinor - ra.next.paidMinor : 0;

  return (<>
    <TPageHead overline="Rent" title="Pay rent" sub="By bank transfer or standing order, quoting your payment reference."
      action={<TLinkButton to="/app/my-home/rent" variant="soft" icon={ReceiptText} small>Statement</TLinkButton>} />
    {q.isError ? <TBanner tone="danger">{(q.error as Error).message}</TBanner> : !ra ? <TCard><SkeletonRows rows={5} cols={2} /></TCard> : (<>
      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr] lg:gap-5">
        <THero overline={due.owing ? 'Amount due now' : due.overline} value={due.amount} note={due.when} alert={due.owing}
          meta={due.owing
            ? <>{ra.totals.monthsOwed} month{ra.totals.monthsOwed === 1 ? '' : 's'} still open.{ra.next && nextLeft > 0 ? <> Then <span className="font-semibold text-ink-900">{formatMoney(nextLeft, cur)}</span> on {formatDate(ra.next.dueDate)}.</> : null}</>
            : 'You are up to date. Thank you.'}>
          <div className="flex flex-wrap items-center gap-2">
            <a href="#bank" className="t-btn t-btn-foil w-full sm:w-auto sm:min-w-[220px]"><Landmark className="h-[18px] w-[18px]" />Pay by bank transfer</a>
          </div>
        </THero>

        <TCard title="What is due" i={1}>
          <TFacts items={[
            { k: 'Due now', v: ra.totals.owingMinor > 0 ? <span className="text-[#8A3F33]">{formatMoney(ra.totals.owingMinor, cur)}</span> : <span className="text-[#3F6340]">Nothing</span> },
            { k: 'Next payment', v: ra.next ? <>{formatMoney(nextLeft || ra.next.amountMinor, cur)}<span className="block text-[12.5px] font-normal text-ink-500">{formatDate(ra.next.dueDate)}</span></> : '—' },
            { k: 'Your rent', v: <>{formatMoney(ra.rentMinor, cur)} a {String(ra.rentPeriod || 'month').toLowerCase()}{dueDay && <span className="block text-[12.5px] font-normal text-ink-500">due on {dueDay}</span>}</> },
            { k: 'Received so far', v: formatMoney(ra.totals.receivedMinor, cur) },
          ]} />
        </TCard>
      </div>

      <div id="bank" className="scroll-mt-24">
        <TCard title="Pay by bank transfer" sub="From your banking app: add Lotus Living as a payee with these details." i={2}>
          {ra.payTo ? (
            <div className="grid gap-2.5 sm:grid-cols-2">
              <CopyField label="Payee" value={ra.payTo.accountName} note={ra.payTo.bankName ? `With ${ra.payTo.bankName}` : undefined} k="payee" copied={copied} onCopy={copy} wide />
              {ra.payTo.sortCode && <CopyField label="Sort code" value={ra.payTo.sortCode} k="sort" copied={copied} onCopy={copy} mono wide={!(ra.payTo.accountNumber || ra.payTo.iban)} />}
              {(ra.payTo.accountNumber || ra.payTo.iban) && <CopyField label={ra.payTo.accountNumber ? 'Account number' : 'IBAN'} value={(ra.payTo.accountNumber ?? ra.payTo.iban)!} k="acct" copied={copied} onCopy={copy} mono wide={!ra.payTo.sortCode} />}
              {ra.payReference && <CopyField label="Payment reference" value={ra.payReference} note="Quote it exactly, so your payment is matched to your home" k="ref" copied={copied} onCopy={copy} mono strong wide />}
            </div>
          ) : (
            <div className="space-y-2.5">
              {ra.payReference && <CopyField label="Payment reference" value={ra.payReference} k="ref" copied={copied} onCopy={copy} mono strong />}
              <TBanner tone="gold" icon={Landmark}>Lotus Living will send you the bank details for your building. <Link to="/app/messages" className="font-semibold underline">Ask in Messages</Link>.</TBanner>
            </div>
          )}
          <ol className="mt-5 grid gap-3 sm:grid-cols-3">
            {['Open your banking app and add a new payee with the details above.', 'Pay the amount due. Quote your payment reference exactly, nothing else.', 'Once it reaches our bank it is matched to your home and shows on your Rent statement.'].map((x, n) => (
              <li key={n} className="flex gap-3 text-[14px] leading-relaxed text-ink-600">
                <span className="t-serif grid h-8 w-8 shrink-0 place-items-center rounded-full bg-bronze-50 text-[17px] text-bronze-700">{n + 1}</span><span>{x}</span>
              </li>
            ))}
          </ol>
        </TCard>
      </div>

      <div className="grid items-start gap-4 md:grid-cols-2 lg:gap-5">
        <TCard i={3} title="Set up a standing order" sub="The easiest way to never miss a month">
          <div className="flex gap-3.5">
            <span className="t-icon-chip"><Repeat className="h-[19px] w-[19px]" /></span>
            <p className="text-[14px] leading-relaxed text-ink-600">
              In your banking app, set a monthly standing order for <span className="font-semibold text-ink-900">{formatMoney(ra.rentMinor, cur)}</span> to the payee above{dueDay ? <>, leaving a few days before <span className="font-semibold text-ink-900">{dueDay}</span> of each month</> : null}, with your payment reference. If your rent changes, update the standing order too.
            </p>
          </div>
        </TCard>
        <TCard i={4} title="Pay by card" action={<span className="rounded-full bg-ink-100 px-2.5 py-1 text-[12px] font-semibold text-ink-600">Coming soon</span>}>
          <div className="flex gap-3.5">
            <span className="t-icon-chip"><CreditCard className="h-[19px] w-[19px]" /></span>
            <p className="text-[14px] leading-relaxed text-ink-600">Card payment is coming. For now, pay by bank transfer or standing order.</p>
          </div>
        </TCard>
      </div>

      <p className="px-1 text-[13px] leading-relaxed text-ink-500">Paid already, or something not right? Your <Link to="/app/my-home/rent" className="font-semibold text-bronze-700 hover:underline">Rent statement</Link> shows every payment we have received — or <Link to="/app/messages" className="font-semibold text-bronze-700 hover:underline">message the team</Link>.</p>
    </>)}
  </>);
}

/** One bank detail with its copy button. `strong`: the payment reference, in gold. */
function CopyField({ label, value, note, k, copied, onCopy, mono, strong, wide }: { label: string; value: string; note?: string; k: string; copied: string | null; onCopy: (k: string, v: string) => void; mono?: boolean; strong?: boolean; wide?: boolean }) {
  const done = copied === k;
  return (
    <div className={`flex min-h-[72px] items-center gap-3 rounded-[16px] px-4 py-3 ${strong ? 'bg-bronze-50 ring-1 ring-bronze-200' : 'bg-ink-50'} ${wide ? 'sm:col-span-2' : ''}`}>
      <div className="min-w-0 flex-1">
        <div className={`text-[12px] font-semibold uppercase tracking-[0.1em] ${strong ? 'text-bronze-700' : 'text-ink-500'}`}>{label}</div>
        <div className={`mt-0.5 break-all text-ink-900 ${mono ? 'tracking-[0.06em]' : ''} ${strong ? 'text-[22px] font-semibold' : 'text-[16px] font-semibold'}`}>{value}</div>
        {note && <div className="mt-0.5 text-[12.5px] leading-snug text-ink-500">{note}</div>}
      </div>
      <button type="button" onClick={() => onCopy(k, value)} aria-label={`Copy ${label.toLowerCase()}`}
        className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13.5px] font-semibold transition ${done ? 'bg-[#E4ECE0] text-[#3F6340]' : 'bg-white text-ink-800 shadow-[var(--shadow-card)] hover:bg-bronze-50'}`}>
        {done ? <><Check className="h-4 w-4" />Copied</> : <><Copy className="h-4 w-4" />Copy</>}
      </button>
      <span className="sr-only" aria-live="polite">{done ? `${label} copied` : ''}</span>
    </div>
  );
}

// ─── Book a space ────────────────────────────────────────────────────────────

/** The shared spaces a resident will book here (the app's Spaces / Amenities) — not built yet, so every one says Soon. */
const SPACES: Array<{ Icon: LucideIcon; name: string; line: string }> = [
  { Icon: Dumbbell, name: 'Residents’ gym', line: 'Drop in, or book a quiet hour' },
  { Icon: Sprout, name: 'Roof garden', line: 'For you and a few guests' },
  { Icon: Laptop, name: 'Co-working room', line: 'Desks, Wi-Fi and a meeting room' },
  { Icon: BedDouble, name: 'Guest suite', line: 'A room for visitors' },
  { Icon: Sofa, name: 'Residents’ lounge', line: 'Somewhere to unwind' },
  { Icon: Clapperboard, name: 'Cinema room', line: 'Film nights downstairs' },
];

function SpacesSection() {
  return (<>
    <TPageHead overline="Book a space" title="Shared spaces" />
    <section className="t-rise relative overflow-hidden rounded-[24px] border border-white/90 p-6 sm:p-8" style={{ background: 'linear-gradient(135deg, #FFFDF9 0%, #EEF5F9 50%, #D9EAF3 100%)', boxShadow: 'var(--shadow-float)' }}>
      <img src="/lotus-mark.svg" alt="" aria-hidden className="pointer-events-none absolute -right-12 -top-12 h-56 w-56 opacity-[0.12]" draggable={false} />
      <p className="relative text-[12px] font-semibold uppercase tracking-[0.14em] text-[#3C627E]">Coming soon</p>
      <h2 className="t-serif relative mt-3 max-w-[560px] text-[30px] leading-[1.1] text-ink-900 sm:text-[38px]">Book your building’s shared spaces from here</h2>
      <p className="relative mt-3 max-w-[520px] text-[15px] leading-relaxed text-ink-600">We are getting this ready. Until then, ask the building team in Messages and they will sort it for you.</p>
      <div className="relative mt-6"><TLinkButton to="/app/messages" icon={MessagesSquare} className="w-full sm:w-auto sm:min-w-[220px]">Message the team</TLinkButton></div>
    </section>

    <TSection>On the way</TSection>
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
      {SPACES.map(({ Icon, name, line }, n) => {
        const sky = n % 2 === 0;
        return (
          <div key={name} className="t-card t-rise overflow-hidden" style={{ '--i': n + 1 } as React.CSSProperties} role="group" aria-label={`${name} — coming soon`}>
            <div className={`relative grid h-[112px] place-items-center overflow-hidden sm:h-[148px] ${sky ? 'bg-[#E6F1F8] text-[#3C627E]' : 'bg-bronze-50 text-bronze-700'}`}>
              <img src="/lotus-mark.svg" alt="" aria-hidden className="pointer-events-none absolute -bottom-10 -right-10 h-32 w-32 opacity-[0.12]" draggable={false} />
              <Icon className="h-8 w-8" strokeWidth={1.6} />
              <span className="absolute left-2.5 top-2.5 rounded-full bg-white/85 px-2.5 py-0.5 text-[11.5px] font-semibold">Soon</span>
            </div>
            <div className="px-4 pb-4 pt-3 sm:px-5">
              <p className="text-[15px] font-semibold leading-snug text-ink-900">{name}</p>
              <p className="mt-0.5 text-[13px] leading-snug text-ink-500">{line}</p>
            </div>
          </div>
        );
      })}
    </div>
    <TBanner tone="sky" icon={CalendarDays}>Not every building has every space. <Link to="/app/messages" className="font-semibold underline">Ask the team</Link> what yours has, or to book one in the meantime.</TBanner>
  </>);
}

// ─── Repairs ─────────────────────────────────────────────────────────────────

/** Our work order states in the resident's words (prototype tStatus). */
const RESIDENT_STATUS: Record<string, [string, string]> = {
  NEW: ['Reported', 'NEW'], TRIAGED: ['Reported', 'NEW'], APPROVED: ['Getting it booked', 'PENDING'], ASSIGNED: ['Booked in', 'SCHEDULED'],
  IN_PROGRESS: ['Being done', 'IN_PROGRESS'], ON_HOLD: ['On hold', 'PARTIAL'], AWAITING_PARTS: ['Waiting for parts', 'PARTIAL'],
  COMPLETED: ['Done', 'COMPLETED'], VERIFIED: ['Done', 'COMPLETED'], CLOSED: ['Done', 'COMPLETED'], CANCELLED: ['No action needed', 'DRAFT'],
};
const CATEGORIES = ['PLUMBING', 'HEATING', 'ELECTRICAL', 'GAS', 'DOORS_WINDOWS', 'DAMP', 'PEST', 'LIFT', 'OTHER'];

function RepairsSection({ t }: { t: R }) {
  const qc = useQueryClient();
  const jobs = useJobs();
  const [reporting, setReporting] = useState(() => new URLSearchParams(window.location.search).get('report') === '1');
  const [form, setForm] = useState({ title: '', description: '', access: '', category: 'PLUMBING', priority: 'ROUTINE' });
  const [photos, setPhotos] = useState<Array<Upload & { preview: string }>>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const report = useMutation({
    mutationFn: async () => {
      const description = [form.description.trim(), form.access.trim() ? `When you can get in: ${form.access.trim()}` : ''].filter(Boolean).join('\n\n');
      const wo = (await api.post<R>('/work-orders', { propertyId: t.propertyId, unitId: t.unitId, tenancyId: t.id, title: form.title, description, accessNotes: form.access.trim() || undefined, category: form.category, priority: form.priority, workType: 'REACTIVE' })).data;
      for (const p of photos) await documentsApi.upload({ resource: 'work_order', resourceId: wo.id, file: { fileName: p.fileName, contentType: p.contentType, dataBase64: p.dataBase64 } });
      return wo;
    },
    onSuccess: (wo) => {
      setReporting(false); setPhotos([]); setDone(wo.reference ?? null);
      setForm({ title: '', description: '', access: '', category: 'PLUMBING', priority: 'ROUTINE' });
      void qc.invalidateQueries({ queryKey: ['my-jobs'] });
    },
  });
  const addPhotos = async (files: FileList | null) => {
    setPhotoError(null);
    for (const f of Array.from(files ?? []).slice(0, 4 - photos.length)) {
      try { const u = await toUpload(f); setPhotos((p) => [...p, { ...u, preview: `data:${u.contentType};base64,${u.dataBase64}` }]); }
      catch (e) { setPhotoError((e as Error).message); }
    }
  };
  const list = jobs.data ?? [];
  const open = list.filter((j) => j.isOpen); const closed = list.filter((j) => !j.isOpen);

  return (<>
    <TPageHead title="Repairs" sub="Tell us what needs fixing — it goes straight to the property team." action={<Button onClick={() => setReporting(true)} icon="Plus">Report a repair</Button>} />
    {done && <TBanner tone="good" icon={CircleCheck}>Thank you — your reference is <span className="font-mono font-semibold">{done}</span>. We will be in touch.</TBanner>}

    <TCard title="Being worked on" sub={jobs.isLoading ? undefined : open.length ? `${open.length} open` : undefined} flush i={1}>
      {jobs.isLoading ? <div className="px-5 pb-4"><SkeletonRows rows={2} cols={1} /></div>
        : !open.length ? (
          <div className="px-5 pb-6 pt-2 text-center">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[#E4ECE0] text-[#3F6340]"><CircleCheck className="h-6 w-6" /></span>
            <p className="mt-3 text-[15px] font-semibold text-ink-900">Nothing outstanding in your flat</p>
            <p className="mt-1 text-[14px] text-ink-500">If something needs fixing, report it and we will book it in.</p>
          </div>
        ) : <TList>{open.map((w) => <JobRow key={w.id} w={w} />)}</TList>}
    </TCard>
    {closed.length > 0 && <TCard title="Finished" sub={`${closed.length} done`} flush i={2}><TList>{closed.slice(0, 10).map((w) => <JobRow key={w.id} w={w} />)}</TList></TCard>}

    <Modal open={reporting} onClose={() => setReporting(false)} title="Report a repair" footer={<><Button variant="secondary" onClick={() => setReporting(false)}>Cancel</Button><Button onClick={() => report.mutate()} disabled={!form.title.trim() || report.isPending}>{report.isPending ? 'Sending…' : 'Send it in'}</Button></>}>
      <div className="space-y-4">
        <Field label="What is wrong?"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="The kitchen tap is dripping" className="w-full" /></Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="What sort of thing"><Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full">{CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' & ').toLowerCase().replace(/^\w/, (x) => x.toUpperCase())}</option>)}</Select></Field>
          <Field label="How urgent"><Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full"><option value="ROUTINE">It can wait a couple of weeks</option><option value="URGENT">It needs doing this week</option><option value="EMERGENCY">Emergency — danger, or no heating / water</option></Select></Field>
        </div>
        <Field label="Tell us a bit more"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="When it started, how bad it is, anything you have tried" className="w-full" /></Field>
        <Field label="When can we get in?"><Input value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })} placeholder="Weekdays after 5pm, or leave a key with the concierge" className="w-full" /></Field>
        <div>
          <span className="mb-2 block text-[13px] font-semibold text-ink-700">Photos of the problem</span>
          <div className="flex flex-wrap gap-2.5">
            {photos.map((p, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden rounded-[14px] border border-ink-200">
                <img src={p.preview} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white" aria-label="Remove photo"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {photos.length < 4 && (
              <label className="grid h-20 w-20 cursor-pointer place-items-center rounded-[14px] border border-dashed border-ink-300 bg-ink-50 text-bronze-600 transition hover:border-bronze-400 hover:bg-bronze-50 focus-within:border-bronze-500">
                <span className="flex flex-col items-center gap-1 text-[12px] font-semibold"><Camera className="h-5 w-5" />Add</span>
                <input type="file" accept="image/*" capture="environment" multiple className="sr-only" onChange={(e) => { void addPhotos(e.target.files); e.target.value = ''; }} />
              </label>
            )}
          </div>
          <p className="mt-2 text-[12.5px] text-ink-500">Up to four. Photos are shrunk on your phone before they are sent.</p>
          {photoError && <p className="mt-1 text-[13px] text-[#8A3F33]">{photoError}</p>}
        </div>
        <TBanner tone="danger" icon={Siren}>A gas leak, a fire or a flood? Ring the out-of-hours number on <Link to="/app/my-home/building" className="font-semibold underline">Building</Link> first.</TBanner>
        {report.isError && <p className="text-[13px] text-[#8A3F33]">{(report.error as Error).message}</p>}
      </div>
    </Modal>
  </>);
}

function JobRow({ w }: { w: R }) {
  const [label, tone] = RESIDENT_STATUS[w.status] ?? ['Reported', 'NEW'];
  const done = tone === 'COMPLETED';
  return (
    <TRow icon={done ? CircleCheck : Wrench} tone={done ? 'good' : 'gold'} title={w.title}
      sub={<>{w.reference} · reported {formatDate(w.reportedAt)}{w.scheduledAt ? ` · booked in for ${formatDate(w.scheduledAt)}` : ''}{w.slaResolveBy && w.isOpen && <span className="block">We aim to have this fixed by {formatDate(w.slaResolveBy)}.</span>}</>}
      trailing={<Badge value={label.toUpperCase().replace(/ /g, '_')} tone={(done ? 'good' : tone === 'IN_PROGRESS' || tone === 'PARTIAL' || tone === 'SCHEDULED' ? 'warning' : 'neutral') as never} />} />
  );
}

// ─── Documents ───────────────────────────────────────────────────────────────

function DocumentsSection() {
  const docs = useMyDocs();
  const [letter, setLetter] = useState<string | null>(null);
  const [allInvoices, setAllInvoices] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const d = docs.data;
  if (docs.isLoading || !d) return <><TPageHead title="Documents" /><TCard><SkeletonRows rows={5} cols={2} /></TCard></>;
  const of = (...cats: string[]) => d.documents.filter((x) => cats.includes(x.category));
  const open = (url: string) => void openStoredFile(url).catch((e: Error) => setErr(e.message));
  const printInvoice = (id: string) => void openInvoiceDocument(id).catch((e: Error) => setErr(e.message));
  const none = (text: string) => <li className="px-5 pb-4 pt-1 text-[14px] leading-relaxed text-ink-500">{text}</li>;

  return (<>
    <TPageHead title="Documents" sub="Your tenancy, your flat and your building — open or download any of them." />
    {err && <TBanner tone="danger">{err}</TBanner>}

    <div className="lg:columns-2 lg:gap-5 [&>section]:mb-4 [&>section]:break-inside-avoid lg:[&>section]:mb-5">
      <DocGroup i={1} title="Your tenancy agreement" empty="Your signed agreement will appear here once we have uploaded it. Ask us any time and we will send a copy." items={of('CONTRACT')} onOpen={open} />

      <TCard i={2} title="Deposit protection" sub={d.deposits.length ? undefined : 'Your certificate appears here once your deposit is protected'} flush>
        <TList>
          {d.deposits.map((x) => (
            <TRow key={x.tenancyId} icon={ShieldCheck} tone="good" title={`Protected with ${x.scheme ?? 'the scheme'}`}
              sub={<><span className="font-mono">{x.schemeReference}</span>{x.protectedOn ? ` · since ${formatDate(x.protectedOn)}` : ''} · <Money minor={x.amountMinor} currency={x.currency} /></>}
              trailing={<Badge value={x.status} />} />
          ))}
          {of('DEPOSIT_CERT', 'PRESCRIBED_INFO').map((x) => <DocRow key={x.id} d={x} onOpen={open} />)}
          {!d.deposits.length && !of('DEPOSIT_CERT', 'PRESCRIBED_INFO').length && none('Nothing yet.')}
        </TList>
      </TCard>

      <DocGroup i={3} title="Inventory" empty="Your inventory and check-in report will appear here. Read it and come back to us within seven days if anything is wrong." items={of('INVENTORY')} onOpen={open} />

      <TCard i={4} title="Safety certificates" sub="For your flat and the building" flush>
        <TList>
          {d.certificates.map((c) => (
            <TRow key={c.id} icon={ShieldCheck} title={c.label} sub={`${c.where === 'flat' ? 'Your flat' : 'The building'}${c.expiresOn ? ` · valid to ${formatDate(c.expiresOn)}` : ''}`}
              href={c.documentUrl ?? undefined}
              trailing={<><Badge value={dueTone(c.state.key)} />{!c.documentUrl && <span className="text-[12.5px] text-ink-500">On request</span>}</>} />
          ))}
          {of('SAFETY_CERT').map((x) => <DocRow key={x.id} d={x} onOpen={open} />)}
          {!d.certificates.length && !of('SAFETY_CERT').length && none('No certificates published yet.')}
        </TList>
      </TCard>

      <TCard i={5} title="Letters" sub="What we have written to you" flush>
        <TList>
          {d.letters.map((l) => <TRow key={l.id} icon={Mail} title={l.subject} sub={l.sentAt ? formatDate(l.sentAt) : undefined} onClick={() => setLetter(l.id)} />)}
          {of('LETTER').map((x) => <DocRow key={x.id} d={x} onOpen={open} />)}
          {!d.letters.length && !of('LETTER').length && none('No letters yet.')}
        </TList>
      </TCard>

      {(d.invoices.length > 0 || of('INVOICE').length > 0) && (
        <TCard i={6} title="Invoices" flush>
          <TList>
            {d.invoices.slice(0, allInvoices ? 24 : 5).map((i) => (
              <TRow key={i.id} icon={ReceiptText} title={invoiceNumberLabel(i.number)} sub={i.issueDate ? formatDate(i.issueDate) : undefined}
                onClick={i.status !== 'DRAFT' ? () => printInvoice(i.id) : undefined} chevron={false}
                trailing={<><Money minor={i.totalMinor} currency={i.currency} className="text-[14px] font-semibold text-ink-900" /><Badge value={i.status} />{i.status !== 'DRAFT' && <Printer className="h-4 w-4 text-bronze-600" aria-label="Print" />}</>} />
            ))}
            {of('INVOICE').map((x) => <DocRow key={x.id} d={x} onOpen={open} />)}
          </TList>
          {d.invoices.length > 5 && <button type="button" onClick={() => setAllInvoices((v) => !v)} className="mx-5 mb-2 mt-1 inline-flex min-h-[44px] items-center text-[14px] font-semibold text-bronze-700 hover:underline">{allInvoices ? 'Show fewer' : `Show all ${Math.min(d.invoices.length, 24)}`}</button>}
        </TCard>
      )}

      <DocGroup i={7} title="Everything else" empty="Your building's handbook and anything else we send you appears here." items={of('OTHER')} onOpen={open} />
    </div>

    <TBanner tone="gold" icon={Lock}>{d.note}</TBanner>
    <LetterModal id={letter} onClose={() => setLetter(null)} />
  </>);
}

function DocGroup({ title, empty, items, onOpen, i }: { title: string; empty: string; items: DocumentItem[]; onOpen: (url: string) => void; i?: number }) {
  return (
    <TCard i={i} title={title} sub={items.length > 1 ? `${items.length} files` : undefined} flush>
      {items.length ? <TList>{items.map((x) => <DocRow key={x.id} d={x} onOpen={onOpen} />)}</TList> : <p className="px-5 pb-4 pt-1 text-[14px] leading-relaxed text-ink-500">{empty}</p>}
    </TCard>
  );
}

function DocRow({ d, onOpen }: { d: DocumentItem; onOpen: (url: string) => void }) {
  return (
    <TRow icon={FileText} title={d.name} onClick={() => onOpen(d.url)} chevron={false}
      sub={`${d.categoryLabel} · ${d.attachedTo}${d.sizeBytes ? ` · ${fileSize(d.sizeBytes)}` : ''}${d.uploadedAt ? ` · ${formatDate(d.uploadedAt)}` : ''}`}
      trailing={<Download className="h-[18px] w-[18px] text-bronze-600" aria-label="Download" />} />
  );
}

function LetterModal({ id, onClose }: { id: string | null; onClose: () => void }) {
  const q = useQuery({ queryKey: ['/documents/mine/letters', id], queryFn: () => documentsApi.letter(id!), enabled: !!id });
  return (
    <Modal open={!!id} onClose={onClose} title={q.data?.subject ?? 'Letter'} size="lg">
      {q.data ? <><p className="mb-3 text-[13px] text-ink-500">Sent {q.data.sentAt ? formatDate(q.data.sentAt) : ''}{q.data.toEmail ? ` to ${q.data.toEmail}` : ''}</p><pre className="whitespace-pre-wrap break-words font-sans text-[15px] leading-relaxed text-ink-800">{q.data.body}</pre></> : <SkeletonRows rows={6} cols={1} />}
    </Modal>
  );
}

// ─── Building ────────────────────────────────────────────────────────────────

function BuildingSection() {
  const q = useMyBuilding();
  const parking = useQuery({ queryKey: ['my-parking'], queryFn: parkingApi.mine });
  if (q.isLoading) return <><TPageHead title="Your building" /><PageLoader /></>;
  const list = q.data ?? [];
  if (!list.length) return <TEmpty icon={Building2} title="No building yet" body="Once your tenancy starts, your building's information appears here." />;
  return (<>{list.map((b) => <BuildingCard key={b.propertyId} b={b} park={parking.data?.find((p) => p.tenancyId === b.tenancyId)} />)}</>);
}

function BuildingCard({ b, park }: { b: MyBuilding; park?: Awaited<ReturnType<typeof parkingApi.mine>>[number] }) {
  const emergency = b.contacts.filter((c) => c.emergency);
  const people = b.contacts.filter((c) => !c.emergency);
  return (<>
    <TPageHead overline="Your building" title={b.propertyName} sub={[b.address, `Flat ${b.unitReference}`].filter(Boolean).join(' · ')} />
    {b.photos?.length > 0 && <TGallery photos={b.photos} title={b.propertyName} />}

    <div className="grid items-start gap-4 lg:grid-cols-2 lg:gap-5">
      <TCard i={1} title="Who to call" flush>
        <TList>
          {[...people, ...emergency].map((c, i) => (
            <TRow key={i} icon={c.emergency ? Siren : Building2} tone={c.emergency ? 'danger' : 'gold'} title={c.name ?? '—'}
              sub={<><span className="block">{c.role}</span>{c.phone && <span className="block font-medium text-ink-700 sm:hidden">{formatPhone(c.phone)}</span>}{c.email && <a href={`mailto:${c.email}`} className="block truncate font-medium text-bronze-700 hover:underline">{c.email}</a>}</>}
              trailing={c.phone ? <CallPill phone={c.phone} urgent={c.emergency} round /> : undefined} />
          ))}
          {!b.contacts.length && <li className="px-5 pb-4 text-[14px] text-ink-500">No numbers published yet.</li>}
        </TList>
      </TCard>

      <TCard i={2} title="Notices" sub={b.notices.length ? undefined : 'Nothing at the moment'} flush>
        {b.notices.length ? <TList>{b.notices.map((n) => (
          <TRow key={n.id} icon={Megaphone} title={n.title}
            sub={<><span className="whitespace-pre-line">{n.body}</span><span className="mt-1 block text-[12px] text-ink-400">{n.propertyId ? '' : 'Every building · '}{n.publishedAt ? formatDate(n.publishedAt) : ''}{n.expiresAt ? ` · until ${formatDate(n.expiresAt)}` : ''}</span></>} />
        ))}</TList>
          : <p className="px-5 pb-4 pt-1 text-[14px] leading-relaxed text-ink-500">When the building team puts up a notice — works, testing, a lift out — it shows here.</p>}
      </TCard>
    </div>

    {b.sections.length ? (
      <div className="md:columns-2 md:gap-5 [&>section]:mb-4 [&>section]:break-inside-avoid md:[&>section]:mb-5">
        {b.sections.map((s, n) => (
          <TCard key={s.key} title={s.title} i={n + 3}>
            <dl className="space-y-3.5">{s.items.map((i) => <div key={i.label}><dt className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-500">{i.label}</dt><dd className="mt-1 whitespace-pre-line text-[14.5px] leading-relaxed text-ink-800">{i.value}</dd></div>)}</dl>
          </TCard>))}
      </div>
    ) : <TBanner tone="gold" icon={Building2}>Lotus Living have not published the building information yet.</TBanner>}

    {park?.bay && park.allocation && (
      <TCard title="Your parking" sub={park.plan?.name ?? undefined}>
        <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
          <div className="flex items-center gap-4">
            <div className="t-serif grid h-14 w-14 shrink-0 place-items-center rounded-[16px] text-[24px] text-white" style={{ background: 'var(--t-foil)' }}>{park.bay.code}</div>
            <div className="text-[14px] text-ink-600">
              <div className="font-semibold text-ink-900">Bay {park.bay.code} · {park.bay.kindLabel}</div>
              <div>{park.allocation.vehicleReg ? <span className="font-mono">{park.allocation.vehicleReg}</span> : 'No vehicle on record'}{park.allocation.permit ? ` · permit ${park.allocation.permit}` : ''}</div>
              <div className="text-ink-500">Yours since {formatDate(park.allocation.fromDate)}</div>
            </div>
          </div>
          {park.plan && <PlanSnippet imageUrl={park.plan.imageUrl} widthPx={park.plan.widthPx} heightPx={park.plan.heightPx} box={park.bay} code={park.bay.code} />}
        </div>
        <p className="mt-4 text-[13px] text-ink-500">Changed car? Tell us the new registration through <Link to="/app/messages" className="font-semibold text-bronze-700 hover:underline">Messages</Link> so the bay stays yours.</p>
      </TCard>
    )}
  </>);
}
